import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export enum UploadStatus {
  Pending = 'Pending',
  Uploading = 'Uploading',
  Success = 'Success',
  Failed = 'Failed',
}

export interface FileItem {
  id: string;
  file: File;
  name: string;

  status: UploadStatus;

  blobId?: string;
  error?: string;
}

export interface UploadedFileItem extends FileItem {
  blobId: string;
}

interface UseFileUploadPanelProps {
  uploadFile: (file: File) => Promise<string>;

  onFilesChange?: (files: UploadedFileItem[]) => void;
}

export const useFileUploadPanel = ({ uploadFile, onFilesChange }: UseFileUploadPanelProps) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const filesRef = useRef<FileItem[]>([]);

  const notifyFilesChange = useCallback(
    (files: FileItem[]) => {
      onFilesChange?.(
        files.filter((file): file is UploadedFileItem => file.status === UploadStatus.Success && !!file.blobId),
      );
    },
    [onFilesChange],
  );

  const updateFiles = useCallback((updater: (prev: FileItem[]) => FileItem[]) => {
    const next = updater(filesRef.current);

    filesRef.current = next;
    setFiles(next);

    return next;
  }, []);

  const updateFile = useCallback(
    (id: string, updates: Partial<FileItem>) => {
      return updateFiles((prev) =>
        prev.map((file) =>
          file.id === id
            ? {
                ...file,
                ...updates,
              }
            : file,
        ),
      );
    },
    [updateFiles],
  );

  const isFileExists = useCallback((id: string) => {
    return filesRef.current.some((file) => file.id === id);
  }, []);

  const uploadSingleFile = useCallback(
    async (item: FileItem) => {
      if (!isFileExists(item.id)) {
        return;
      }

      updateFile(item.id, {
        status: UploadStatus.Uploading,
      });

      try {
        const blobId = await uploadFile(item.file);

        if (!isFileExists(item.id)) {
          return;
        }

        const nextFiles = updateFile(item.id, {
          status: UploadStatus.Success,
          blobId,
        });

        notifyFilesChange(nextFiles);
      } catch (error) {
        if (!isFileExists(item.id)) {
          return;
        }

        updateFile(item.id, {
          status: UploadStatus.Failed,
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    },
    [uploadFile, updateFile, isFileExists],
  );

  const addFiles = useCallback(
    async (selectedFiles: File[]) => {
      const uploadItems: FileItem[] = selectedFiles.map((file) => ({
        id: nanoid(),
        file,
        name: file.name,
        status: UploadStatus.Pending,
      }));

      updateFiles((prev) => [...prev, ...uploadItems]);

      for (const item of uploadItems) {
        await uploadSingleFile(item);
      }
    },
    [updateFiles, uploadSingleFile],
  );

  const deleteFile = useCallback(
    (id: string) => {
      const nextFiles = updateFiles((prev) => prev.filter((file) => file.id !== id));
      notifyFilesChange(nextFiles);
    },
    [updateFiles, notifyFilesChange],
  );

  const clearFiles = useCallback(() => {
    updateFiles(() => []);
    notifyFilesChange([]);
  }, [updateFiles, notifyFilesChange]);

  return {
    files,

    addFiles,
    deleteFile,
    clearFiles,
  };
};
